import React, { useEffect, useState, useRef } from "react";
import Header2 from "../components/Header2";
import Footer from "../components/Footer";

import BannerRetroflex from "../assets/banner-retroflex.jpg";
import "./Equipamentos.css";

/* ===== Dados (JSON simples) ===== */
import equipamentos from "../data/equipamentos.json";

/* ---------- Utils ---------- */
const normalize = (s = "") =>
  s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

const slug = (s = "") =>
  normalize(s).replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/* Dicionário por nome — com trim para nomes com espaço no fim no JSON */
const D = Object.fromEntries((equipamentos || []).map((m) => [String(m.nome).trim(), m]));

/* Grupos fixos (nav) — incluindo Abdômen */
const grupos = [
  { id: "biceps", rotulo: "Bíceps" },
  { id: "triceps", rotulo: "Tríceps" },
  { id: "ombro", rotulo: "Ombro" },
  { id: "costas", rotulo: "Costas" },
  { id: "peito", rotulo: "Peito" },
  { id: "quadriceps", rotulo: "Quadríceps" },
  { id: "posterior", rotulo: "Posterior" },
  { id: "adutor", rotulo: "Adutor" },
  { id: "gluteo", rotulo: "Glúteo" },
  { id: "panturrilha", rotulo: "Panturrilha" },
  { id: "abdomen", rotulo: "Abdômen" },
  { id: "polias", rotulo: "Polias" }
];

/* ---------- Carrossel simples (2+ imagens) ---------- */
function ImageCarousel({ images = [], alt, interval = 3500 }) {
  const safe =
    images && images[0] ? (images.length >= 2 ? images : [images[0], images[0]]) : [];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (safe.length < 1) return;
    const id = setInterval(() => setIdx((i) => (i + 1) % safe.length), interval);
    return () => clearInterval(id);
  }, [safe.length, interval]);

  if (safe.length < 1) return null;

  return (
    <div className="carousel">
      {safe.map((src, i) => (
        <img
          key={i}
          src={src}
          alt={`${alt} — imagem ${i + 1}`}
          className={`carousel__img ${i === idx ? "is-active" : ""}`}
          loading="lazy"
        />
      ))}
      <div className="carousel__dots">
        {safe.map((_, i) => (
          <span key={i} className={`carousel__dot ${i === idx ? "is-active" : ""}`} />
        ))}
      </div>
    </div>
  );
}

/* ---------- Card reutilizável ---------- */
function EquipCard({ data, section, reverso = false }) {
  if (!data) return null;
  const { nome, descricao, grupoMuscular, fabricante, imagem, imagem2 } = data;
  const anchorId = `${slug(nome)}-${section || "geral"}`;

  return (
    <div
      id={anchorId}
      className={`equipamento-linha ${reverso ? "reverso" : ""}`}
      data-equip-name={nome}
      data-fabricante={fabricante || ""}
      data-section={section || ""}
    >
      <div className="equipamento-imagem-lateral">
        <ImageCarousel images={[imagem, imagem2]} alt={nome} />
      </div>
      <div className="equipamento-texto">
        <h2 className="equipamento-nome">{nome}</h2>
        <p className="equipamento-descricao">{descricao}</p>
        <div className="equipamento-detalhes">
          {grupoMuscular && (
            <p>
              <strong>Grupo Muscular:</strong> {grupoMuscular}
            </p>
          )}
          {fabricante && (
            <p>
              <strong>Fabricante:</strong> {fabricante}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Busca por máquinas ---------- */
function SearchEquipmentBar({
  items,
  onNavigate,
  placeholder = "Buscar máquina...",
  dense = false,
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const qn = normalize(q);
  const results = qn
    ? items
        .filter(
          (it) =>
            normalize(it.label).includes(qn) ||
            normalize(it.meta || "").includes(qn)
        )
        .slice(0, 12)
    : [];

  useEffect(() => {
    function onDocClick(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function goFirstIfEnter(e) {
    if (e.key === "Enter" && results[0]) {
      onNavigate(results[0].id);
      setOpen(false);
      e.currentTarget.blur();
    }
    if (e.key === "Escape") {
      setOpen(false);
      setQ("");
      e.currentTarget.blur();
    }
  }

  return (
    <div className={`searchbar ${dense ? "searchbar--dense" : ""}`} ref={wrapRef}>
      <input
        className="searchbar__input"
        type="text"
        placeholder={placeholder}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={goFirstIfEnter}
        aria-label={placeholder}
      />
      <span className="searchbar__icon" aria-hidden="true">
        ⌕
      </span>

      {open && results.length > 0 && (
        <ul className="searchbar__dropdown" role="listbox">
          {results.map((r) => (
            <li key={r.id}>
              <button
                className="searchbar__item"
                onClick={() => {
                  onNavigate(r.id);
                  setOpen(false);
                }}
                role="option"
                aria-selected="false"
              >
                {r.label}
                {r.section ? ` · ${r.section}` : ""} {r.meta ? ` — ${r.meta}` : ""}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ---------- Menu Flutuante (aberto por padrão) ---------- */
function FloatingMuscleMenu({ items, searchItems, onNavigate }) {
const [open, setOpen] = useState(false);

  const [active, setActive] = useState(items?.[0]?.id || "");
  const panelRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (!panelRef.current) return;
      if (panelRef.current.contains(e.target)) return;
      const btn = document.getElementById("fab-muscles-btn");
      if (btn && btn.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const sections = items.map((i) => document.getElementById(i.id)).filter(Boolean);
    if (!sections.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((en) => en.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target?.id) setActive(visible.target.id);
      },
      { rootMargin: "-40% 0px -40% 0px", threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, [items]);

  const goTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setOpen(true);
  };

  return (
    <div className="fab" aria-label="Acessar musculaturas rapidamente">
      <button
        id="fab-muscles-btn"
        className="fab__btn"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((o) => !o)}
        title="Abrir menu"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="currentColor"
            d="M3 10h2V7h2v10H5v-3H3v-4Zm16 0h2v4h-2v3h-2V7h2v3ZM9 9h6v6H9V9Z"
          />
        </svg>
      </button>

      {open && (
        <div className="fab__panel" ref={panelRef} role="dialog" aria-modal="true" aria-label="Navegação">
          <div className="fab__header">
            <strong>Navegação</strong>
            <button className="fab__close" onClick={() => setOpen(false)} aria-label="Fechar">
              ×
            </button>
          </div>

          <div className="fab__search">
            <SearchEquipmentBar
              items={searchItems}
              onNavigate={(id) => {
                onNavigate(id);
                setOpen(true);
              }}
              placeholder="Buscar máquina..."
              dense
            />
          </div>

          <div className="fab__list">
            <button className="fab__item fab__item--top" onClick={goTop}>
              Início da página
            </button>
            {items.map((it) => (
              <a
                key={it.id}
                className={`fab__item ${active === it.id ? "is-active" : ""}`}
                href={`#${it.id}`}
                onClick={() => setOpen(true)}
              >
                {it.rotulo}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const Equipamentos = () => {
  const [searchItems, setSearchItems] = useState([]);

  // Navega e dá destaque no destino
  const goTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("jump-highlight");
    setTimeout(() => el.classList.remove("jump-highlight"), 1600);
  };

  // Indexa os cards renderizados (para busca)
  useEffect(() => {
    const nodes = Array.from(
      document.querySelectorAll(".equipamento-linha[id][data-equip-name]")
    );
    const items = nodes.map((n) => ({
      id: n.id,
      label: n.getAttribute("data-equip-name") || "",
      meta: n.getAttribute("data-fabricante") || "",
      section: n.getAttribute("data-section") || "",
    }));
    setSearchItems(items);
  }, []);

  return (
    <>
      <Header2 />
      <section className="equipamentos-section" id="topo">
        {/* BANNER */}
        <div
          className="equipamentos-banner"
          style={{ backgroundImage: `url(${BannerRetroflex})` }}
        >
          <div className="banner-overlay">
            <div className="banner-conteudo">
              <h1 className="banner-titulo">EQUIPAMENTOS</h1>
              <p className="banner-descricao">
                Estrutura por musculatura, busca por máquina — encontre rápido o que interessa.
              </p>
            </div>
          </div>
          <div className="banner-fade-bottom" />
        </div>

        {/* Faixa separadora */}
        <div className="banner-separacao" />

        {/* Busca no topo */}
        <div className="muscle-search container">
          <SearchEquipmentBar
            items={searchItems}
            onNavigate={goTo}
            placeholder="Buscar máquina (ex.: Chest Press, Low Row, Hack Squat...)"
          />
        </div>

        {/* NAV por musculatura */}
        <nav className="muscle-nav container" aria-label="Navegação por musculatura">
          {grupos.map((g) => (
            <a key={g.id} className="muscle-chip" href={`#${g.id}`}>
              {g.rotulo}
            </a>
          ))}
        </nav>

        {/* ===== SESSÕES ===== */}
        <div className="muscle-groups">
          {/* BÍCEPS */}
          <section id="biceps" className="grupo container">
            <h2 className="grupo__title">Bíceps</h2>
            <div className="grupo__content">
              <EquipCard data={D["Nautilus Gen 1 Multi bicep MC2"]} section="biceps" />
              <EquipCard data={D["Flex Fitness Classic Line Bicep Curl"]} section="biceps" reverso />
              <EquipCard data={D["Icarian Flite Bicep Curl"]} section="biceps" />
              <EquipCard data={D["Hammer Strength Free Weight Seated Arm Curl"]} section="biceps" reverso />
            </div>
          </section>

          {/* TRÍCEPS */}
          <section id="triceps" className="grupo container">
            <h2 className="grupo__title">Tríceps</h2>
            <div className="grupo__content">
              <EquipCard data={D["Nautilus Next Generation Multi Tricep"]} section="triceps" />
              <EquipCard data={D["Flex Fitness Classic Line Tricep Extension"]} section="triceps" reverso />
              <EquipCard data={D["Hammer Strength Plaloaded Seated Dip"]} section="triceps" />
            </div>
          </section>

          {/* OMBRO */}
          <section id="ombro" className="grupo container">
            <h2 className="grupo__title">Ombro</h2>
            <div className="grupo__content">
              <EquipCard data={D["Cybex VR Shoulder Press"]} section="ombro" />
              <EquipCard data={D["Nautilus Overhead Press"]} section="ombro" reverso />
              <EquipCard data={D["Nautilus Power Plus Plate Loaded Lateral Raise"]} section="ombro" />
              <EquipCard data={D["Cybex Classic Lateral Raise"]} section="ombro" reverso />
              <EquipCard data={D["Cybex Plateloaded 5000 Series Shoulder press"]} section="ombro" />
              <EquipCard data={D["Hammer Strength Rear Delt ILRD Iso-lateral"]} section="ombro" reverso />
            </div>
          </section>

          {/* COSTAS */}
          <section id="costas" className="grupo container">
            <h2 className="grupo__title">Costas</h2>
            <div className="grupo__content">
              <EquipCard data={D["Hammer Strength Iso Lateral Low Row"]} section="costas" />
              <EquipCard data={D["Hammer Strength IL Lat Pullover"]} section="costas" reverso />
              <EquipCard data={D["Hammer Strength Plateloaded Gen 2 ILROW"]} section="costas" />
              <EquipCard
                data={D["Nautilus Next Generation Chain Driven Super Pullover W/Range Limiter"]}
                section="costas"
                reverso
              />
              <EquipCard data={D["Cybex VR Rowing"]} section="costas" />
              <EquipCard data={D["Cybex 5301 Low Pull Station “The Exodia”"]} section="costas" reverso />
              <EquipCard data={D["Magnum Selectorized Biangular Lat Row"]} section="costas" />
              <EquipCard data={D["Life Fitness Pro Strength Lat Pulldown"]} section="costas" reverso />
              <EquipCard data={D["Flex Fitness Lat Pull"]} section="costas" />
              <EquipCard data={D["Paramount Performance Plate Loaded Lever Row"]} section="costas" reverso />
              <EquipCard data={D["Cybex Plateloaded 5000 Series T-Bar Row"]} section="costas" />
              <EquipCard data={D["Hammer Strength Iso Lateral D.Y row"]} section="costas" reverso />
              <EquipCard data={D["Icarian Selectorized Back Extension"]} section="costas" />
            </div>
          </section>

          {/* PEITO */}
          <section id="peito" className="grupo container">
            <h2 className="grupo__title">Peito</h2>
            <div className="grupo__content">
              <EquipCard data={D["Nautilus Vertical Chest NS2VC"]} section="peito" />
              <EquipCard data={D["Nautilus Next Generation Chest"]} section="peito" reverso />
              <EquipCard data={D["Nautilus 2ST Bench Press"]} section="peito" />
              <EquipCard data={D["Cybex Plateloaded 5000 series Advanced Chest"]} section="peito" reverso />
              <EquipCard data={D["Icarian Olympic Incline Bench"]} section="peito" />
              <EquipCard data={D["Hammer Strength Iso Lateral Seated Bench Press"]} section="peito" reverso />
              <EquipCard data={D["Life Fitness Pro Strength Chest Press"]} section="peito" />
            </div>
          </section>

          {/* QUADRÍCEPS */}
          <section id="quadriceps" className="grupo container">
            <h2 className="grupo__title">Quadríceps</h2>
            <div className="grupo__content">
              <EquipCard
                data={D["Technogym Isotonic Lux Line Horizontal Leg Press (AKA Cindy)"]}
                section="quadriceps"
              />
              <EquipCard data={D["Cybex Plate Loaded Hack Squat"]} section="quadriceps" reverso />
              <EquipCard data={D["Body Masters Plate Loaded Hack Squat"]} section="quadriceps" />
              <EquipCard data={D["Body Masters Power Squat"]} section="quadriceps" reverso />
              <EquipCard data={D["Cybex Vr Leg Extension"]} section="quadriceps" />
              <EquipCard data={D["Nautilus Power Plus Plate Loaded Leg Extension"]} section="quadriceps" reverso />
              <EquipCard data={D["Strive Plate Loaded Leg Extension Original Style"]} section="quadriceps" />
              <EquipCard data={D["Cybex Plateloaded 5000 series Squat Press"]} section="quadriceps" reverso />
              <EquipCard data={D["Cybex Plateloaded 5000 Series Leg Press"]} section="quadriceps" />
              <EquipCard data={D["Flex Fitness Olympic Squat Rack"]} section="quadriceps" reverso />
              <EquipCard data={D["Cybex Plateloaded 5000 Smith Machine"]} section="quadriceps" />
            </div>
          </section>

          {/* POSTERIOR */}
          <section id="posterior" className="grupo container">
            <h2 className="grupo__title">Posterior</h2>
            <div className="grupo__content">
              <EquipCard data={D["Life Fitness Pro Strength Seated Leg Curl"]} section="posterior" />
              <EquipCard data={D["Technogym Isotonic Lux Line Leg Curl"]} section="posterior" reverso />
              <EquipCard data={D["Cybex Plateloaded 5000 series Kneeling Leg Curl"]} section="posterior" />
              <EquipCard data={D["Nautilus Gen 1 Leg Curl"]} section="posterior" reverso />
              <EquipCard data={D["Tuffstuff Proformance Plus Glute-Ham Bench"]} section="posterior" />
            </div>
          </section>

          {/* ADUTOR */}
          <section id="adutor" className="grupo container">
            <h2 className="grupo__title">Adutor</h2>
            <div className="grupo__content">
              <EquipCard data={D["Cybex Classic Adduction"]} section="adutor" />
            </div>
          </section>

          {/* GLÚTEO */}
          <section id="gluteo" className="grupo container">
            <h2 className="grupo__title">Glúteo</h2>
            <div className="grupo__content">
              <EquipCard data={D["Nautilus Leverage Glute Drive"]} section="gluteo" />
              <EquipCard data={D["Life Fitness Pro Hip Abduction"]} section="gluteo" reverso />
            </div>
          </section>

          {/* PANTURRILHA */}
          <section id="panturrilha" className="grupo container">
            <h2 className="grupo__title">Panturrilha</h2>
            <div className="grupo__content">
              <EquipCard data={D["Cybex Classic Standing Calf Raise"]} section="panturrilha" />
              <EquipCard data={D["Flex Fitness Classic Line 45 Degree Donkey Calf"]} section="panturrilha" reverso />
            </div>
          </section>

          {/* ABDÔMEN */}
          <section id="abdomen" className="grupo container">
            <h2 className="grupo__title">Abdômen</h2>
            <div className="grupo__content">
              <EquipCard data={D["Technogym Isotonic Lux Line Abdominal Crunch R.O.M."]} section="abdomen" />
              <EquipCard data={D["Life Fitness Club Series Free Weight Leg Raise"]} section="abdomen" reverso />
            </div>
          </section>

          {/* POLIAS */}
          <section id="polias" className="grupo container">
            <h2 className="grupo__title">Polias</h2>
            <div className="grupo__content">
              <EquipCard data={D["Body Masters Cable Crossover c/ Barra Fixa"]} section="polias" />
              <EquipCard data={D["Cybex Adjustable Cable Cross"]} section="polias" reverso />
            </div>
          </section>
        </div>

        <FloatingMuscleMenu items={grupos} searchItems={searchItems} onNavigate={goTo} />
      </section>
      <Footer />
    </>
  );
};

export default Equipamentos;
